<template>
  <div class="exam-content">
    <div class="exam-content__modal">
      <div class="exam-content__ilu-wrapper">
        <div class="exam-content__wrapper">
          <TitleBar
            :title="titleLabel"
            type="exam"
            :exam-layout-v2="examLayoutV2"
            :show-submit="!isICCA"
            :current-time="currentTime"
            :can-submit="canSubmit"
            :current-step="currentExamStep"
            :is-last-question="currentQuestion === lastQuestion"
            :steps="examSteps"
            @submit="commitAnswerAndSubmit"
            @close-modal="$emit('close-modal')"
            @close-timer="showCurrentTime = false"
          />
          <div v-if="examLayoutV2" class="exam-content__time-wrapper">
            <TimerWidget
              :current-time="currentTime"
              :exam-layout-v2="examLayoutV2"
            />
          </div>
          <div v-if="examLayoutV2 && !isICCA" class="exam-content__lab-block">
            <p
              class="exam-content__lab-block-instructions"
              v-html="labInstructions"
            />
            <PTACardCurrentSession
              :class="[
                'exam-content__lab-block-lab-container',
                isBeta && 'exam-content__lab-block-lab-container--beta',
              ]"
              :current-lab="examLab"
              :is-e-d-a="isEDA"
              :is-w-p-t="isEWPT"
              :is-c-d-f-p="isCDFP"
              :current-session="currentSession"
              :set-lab-status="setLabStatus"
              :context="context"
              :mixpanel-event-payload="mixpanelEventPayload"
              :labs-request-payload="labsRequestPayload"
              @close-modal="$emit('close-modal')"
              @status-has-changed="changeCurrentLabStatus"
            />
            <div v-if="isBeta" class="exam-content__lab-block-beta">
              <span class="exam-content__lab-block-beta-pill"> BETA </span>
              <span> This lab is in Beta mode. </span>
            </div>
          </div>
          <div
            :class="[
              'exam-content__wrapper-limited',
              examLayoutV2 &&
                currentExamStep === examSteps.QUESTIONS &&
                'exam-content__wrapper-limited--layout-v2',
            ]"
          >
            <div
              v-if="currentExamStep === examSteps.QUESTIONS"
              class="exam-content__left-column"
            >
              <QuestionNavigation
                class="exam-content__navigation"
                :questions="questionsSummary"
                :show-nav-info="!questionsLab.length"
                :current-question="currentQuestion"
                :exam-layout-v2="examLayoutV2"
                @move-to-question="moveToQuestion"
              />
              <QuestionNavigation
                v-if="questionsLab.length"
                class="exam-content__navigation"
                show-nav-info
                :questions="questionsLab"
                custom-title="Lab questions"
                :current-question="currentQuestion"
                :exam-layout-v2="examLayoutV2"
                @move-to-question="moveToQuestion"
              />
            </div>
            <div
              v-if="currentExamStep === examSteps.QUESTIONS"
              ref="examcontent"
              :class="
                examLayoutV2
                  ? 'exam-content__content-wrapper--layout-v2'
                  : 'exam-content__content-wrapper'
              "
            >
              <div
                v-if="currentQuestion.is_dynamic"
                class="exam-content__dynamic-flag"
              >
                <DynamicFlag />
              </div>
              <div
                :class="
                  examLayoutV2
                    ? 'exam-content__content--layout-v2'
                    : 'exam-content__content'
                "
              >
                <Question
                  v-if="!isExamComplete"
                  :class="
                    examLayoutV2
                      ? 'exam-content__question--layout-v2'
                      : 'exam-content__question'
                  "
                  :current-question="currentQuestion"
                  :total-questions="totalQuestions"
                  :current-position="currentPosition + 1"
                />
                <Answers
                  v-if="!isExamComplete"
                  ref="answers"
                  class="exam-content__answers"
                  v-bind="{
                    currentQuestion,
                    lastQuestion,
                    firstQuestion,
                    showActionButtons: true,
                    isBeta,
                    isLoading,
                    lastSavedDate,
                    examLayoutV2,
                    attempt,
                    isLabRunning,
                  }"
                  @answer-selected="commitAnswer"
                  @move-previous-question="movePreviousQuestion"
                  @open-question-feedback="openQuestionFeedback"
                  @flag-question="flagQuestion"
                  @set-disabled="setCTADisabled"
                />
              </div>
            </div>
            <PracticalExam
              v-if="currentExamStep === examSteps.PRACTICE"
              :attempt="attempt"
              :context="context"
              :certification="certification"
              :user-lab-attempt="attempt.user_lab_attempt"
              :labs-request-payload="labsRequestPayload"
              @update-practice-state="updatePracticeState"
            />
            <ReviewExam
              v-if="currentExamStep === examSteps.REVIEW"
              :answered-questions="answeredQuestions"
              :flagged-questions="flaggedQuestions"
              :unanswered-questions="unansweredQuestions"
              :total-questions="totalQuestions"
              :user-lab-attempt="attempt.user_lab_attempt"
              :steps="examSteps"
              @go-to-step="goToStep"
            />
            <ExamFooter
              v-if="!examLayoutV2 || isICCA"
              :current-step="currentExamStep"
              :steps="examSteps"
              :last-question-position="currentPosition + 1 === totalQuestions"
              :practice-status="practiceStatus"
              :can-go-to-practice="!isLoading"
              :can-go-to-review="canGoToReview"
              :can-submit="canSubmit"
              :exam-layout-v2="examLayoutV2"
              @go-to-step="goToStep"
              @submit="commitAnswerAndSubmit"
              @save-answers-and-practice="commitAnswerAndPractice"
              @save-answers-and-review="commitAnswerAndReview"
            />

            <div v-if="openFeedbackModal" class="exam-content__feedback">
              <Feedback
                :title="feedbackTitle"
                :feedback-holder="feedbackHolder"
                @give-feedback="saveFeedback"
                @close-modal="closeFeedback"
              >
                While our exams are in beta, we gather feedback from a selected
                group of students.<br />
                Your feedback will help us improve before launching.
              </Feedback>
            </div>
            <div v-if="openAlertModal" class="exam-content__alert">
              <AlertModal :show-close="false">
                <div
                  v-if="unansweredQuestions"
                  class="exam-content__alert__warning"
                >
                  <WaringOrangeIcon class="exam-content__alert__warning-icon" />
                  You have {{ unansweredQuestions }} unanswered questions and
                  {{ flaggedQuestions }} pinned questions.
                </div>
                <h1 class="exam-content__alert__title">
                  Are you sure you're ready to submit this exam?
                </h1>
                <p class="exam-content__alert__subtitle">
                  This action can't be undone
                </p>
                <vue-loaders
                  v-if="isLoading"
                  name="line-scale"
                  color="#ef7d22"
                />
                <div v-else class="exam-content__alert-cta-wrapper">
                  <button
                    :disabled="isLoading"
                    class="btn btn--primary exam-content__alert__cta"
                    @click="submitAttempt"
                  >
                    Submit exam
                  </button>
                  <div
                    class="btn btn--tertiary exam-content__alert--close"
                    :disabled="isLoading"
                    @click="openAlertModal = false"
                  >
                    Go Back
                  </div>
                </div>
              </AlertModal>
            </div>
            <div v-if="openFinishedModal" class="exam-content__finished">
              <AlertModal
                @close-modal="isBeta ? generalFeedback() : $emit('close-modal')"
              >
                <SuccessIcon class="exam-content__alert__success" />
                <h1 class="exam-content__alert__title">
                  Thank you for your submission
                </h1>
                <p class="exam-content__alert__subtitle">
                  {{
                    isBeta
                      ? 'We are currently reviewing all beta test submissions before grading your exam. Once the team has finished reviewing all exams, you will receive an email from our staff detailing where you can see your exam results and a detailed breakdown of your score. We anticipate all results should be received approximately two months after the beta has concluded.'
                      : "We're evaluating your answers. You'll be notified when the results are in."
                  }}
                </p>
                <button
                  class="btn btn--primary exam-content__alert__cta"
                  @click="generalFeedback"
                >
                  Next
                </button>
              </AlertModal>
            </div>
            <div v-if="openThankYouModal" class="exam-content__finished">
              <AlertModal @close-modal="$emit('close-modal')">
                <SuccessIcon class="exam-content__alert__success" />
                <h1 class="exam-content__alert__title">
                  Thank you for your feedback
                </h1>
                <p class="exam-content__alert__subtitle">
                  Your comments have been noted.
                </p>
                <button
                  class="btn btn--primary exam-content__alert__cta"
                  @click="$emit('close-modal')"
                >
                  Close
                </button>
              </AlertModal>
            </div>
            <div v-if="runOutOfTime" class="exam-content__finished">
              <AlertModal @close-modal="$emit('close-modal')">
                <div class="exam-content__alert__error">
                  <XCircleIcon class="exam-content__alert__error-icon" />
                  The time clock has expired.
                </div>
                <h1 class="exam-content__alert__title">
                  You failed to submit your exam in the allotted amount of time.
                </h1>
                <button
                  class="btn btn--primary exam-content__alert__cta"
                  @click="$emit('close-modal')"
                >
                  Close
                </button>
              </AlertModal>
            </div>
            <ExamScoreModal
              v-if="showScoreModal"
              :attempt="attempt"
              @close-score-modal="$emit('close-modal')"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { highlightAllUnder } from 'prismjs'
import { intervalToDuration, differenceInMilliseconds } from 'date-fns'
import { XCircleIcon } from 'vue-feather-icons'
import CONSTANTS from '@/constants'
import SuccessIcon from '@/assets/images/certification/submission-success-icon.svg'
import WaringOrangeIcon from '@/assets/images/certification/warning-orange-icon.svg'
import Feedback from '@/components/certifications/Feedback.vue'
import AlertModal from '@/components/certifications/AlertModal.vue'
import QuestionNavigation from '@/components/certifications/QuestionNavigation.vue'
import ExamScoreModal from '@/components/modals/ExamScoreModal.vue'
import PracticalExam from '@/components/certifications/PracticalExam.vue'
import ReviewExam from '@/components/certifications/ReviewExam.vue'
import ExamFooter from '@/components/certifications/ExamFooter.vue'
import PTACardCurrentSession from '@/components/modals/lab-modal/PTACardCurrentSession.vue'
import TimerWidget from '@/components/certifications/TimerWidget.vue'
import TitleBar from './quiz-modal/TitleBar.vue'
import Question from './quiz-modal/Question.vue'
import Answers from './quiz-modal/Answers.vue'
import 'prismjs/components/prism-python'
import DynamicFlag from './quiz-modal/DynamicFlag.vue'

export default {
  name: 'ExamModal',
  components: {
    TitleBar,
    Question,
    Answers,
    Feedback,
    AlertModal,
    SuccessIcon: { ...SuccessIcon, name: 'SuccessIcon' },
    WaringOrangeIcon: { ...WaringOrangeIcon, name: 'WaringOrangeIcon' },
    QuestionNavigation,
    ExamScoreModal,
    PracticalExam,
    XCircleIcon,
    ExamFooter,
    ReviewExam,
    DynamicFlag,
    PTACardCurrentSession,
    TimerWidget,
  },
  props: {
    certification: {
      type: Object,
      required: true,
    },
    voucherId: {
      type: String,
      required: true,
    },
    attempt: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    parentType: {
      type: String,
      required: false,
      default: () => 'isolated',
    },
    context: {
      type: String,
      required: true,
    },
    parent: {
      type: Object,
      required: false,
    },
    isICCA: {
      type: Boolean,
      required: false,
      default: false,
    },
    examLayoutV2: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      currentPosition: 0,
      selectedAnswers: [],
      isExamComplete: false,
      isExamPassed: false,
      finalScore: {},
      isLoading: false,
      openFeedbackModal: false,
      feedbackTitle: '',
      currentTime: null,
      runningTimeIntervalId: null,
      runningOfOutTimeIntervalId: null,
      openAlertModal: false,
      openFinishedModal: false,
      openThankYouModal: false,
      questionsWithAnswers: null,
      runOutOfTime: false,
      feedbackHolder: '',
      currentDuration: null,
      showCurrentTime: false,
      lastSavedDate: null,
      showScoreModal: false,
      currentExamStep: 'Questions',
      practiceStatus: CONSTANTS.CERTIFICATIONS.PRACTICE_STATUS.IDDLE,
      canSubmit: true,
    }
  },
  computed: {
    ...mapState({
      examLab: state => state.labs.currentLab,
      currentSession: state => state.labs.currentLabSession,
    }),
    minScoreToPass() {
      return `${Math.floor(this.attempt.min_global_score_to_pass * 100)}%`
    },
    titleLabel() {
      if (!this.examLayoutV2) return this.title
      return this.attempt.certification.name
    },
    questions() {
      return this.attempt.questions
    },
    questionsSummary() {
      const { questions } = this

      return this.isEDA || this.isCDFP
        ? questions.filter(q => !q.is_lab_related)
        : questions
    },
    questionsLab() {
      const { questions } = this

      return this.isEDA || this.isCDFP
        ? questions.filter(q => q.is_lab_related)
        : []
    },
    questionsSorted() {
      const { questionsSummary, questionsLab } = this
      return [...questionsSummary, ...questionsLab]
    },
    totalQuestions() {
      return this.questions.length
    },
    currentQuestion() {
      return this.questionsSorted[this.currentPosition]
    },
    isEDA() {
      return (
        this.attempt?.certification?.id === CONSTANTS.CERTIFICATIONS.IDS.EDA
      )
    },
    isEWPT() {
      return (
        this.attempt?.certification?.id === CONSTANTS.CERTIFICATIONS.IDS.eWPT
      )
    },
    isCDFP() {
      return (
        this.attempt?.certification?.id === CONSTANTS.CERTIFICATIONS.IDS.CDFP
      )
    },
    labInstructions() {
      switch (true) {
        case this.isEDA:
          return `Instructions: Please answer the following exam questions. We highly recommend completing the multiple-choice section of the exam before moving on to the lab portion. When you make it to the lab questions, please read the instructions in the box below to open the lab environment. You will not need the lab open for the first part of the exam. To pass the eEDA, you must receive an overall exam score of at least ${this.minScoreToPass}.`
        case this.isEWPT:
          return `Instructions: Please begin your testing experience by reviewing the <a href="https://drive.google.com/file/d/1WvS9oFLBpclnGXK7xy-mRrgGgcRM5yms/view" target="_blank">“eWPT Letter of Engagement.”</a> This document contains crucial information you will need to complete this exam. Next, please proceed to the box below to open your exam’s lab environment. Please note that to pass the eWPT, you must receive an overall exam score of at least ${this.minScoreToPass}.`
        case this.isCDFP:
          return `Please begin your testing experience by reviewing the <a href="https://drive.google.com/file/d/1-HQM6vEMqW2MJEfBfjqGNt-FxOSY5aPH/view" target="_blank">“eCDFP Letter of Engagement.”</a> This document contains crucial information you will need to complete this exam. When you have completed the lab-independent questions on this exam , please proceed to the box below to open your exam’s lab environment. Please note that to pass the eCDFP, you must receive an overall exam score of at least ${this.minScoreToPass}.`
        default:
          return `Instructions: Please start your exam by activating the lab in the box below. Once you have chosen the region closest to you and clicked "Start Lab", the lab will generate in a secondary tab. Please keep both of these tabs open for the duration of the exam. Once the lab has generated, you may begin answering the multiple choice questions on this exam. Please note that the multiple choice questions are dependent on the lab environment. You must score a minimum of ${this.minScoreToPass} to pass.`
      }
    },
    lastQuestion() {
      return this.questionsSorted[this.questionsSorted.length - 1]
    },
    firstQuestion() {
      return this.questionsSorted[0]
    },
    percentComplete() {
      return Math.floor(
        ((this.currentPosition + 1) / this.totalQuestions) * 100
      ).toString()
    },
    questionsStatus() {
      const qStatus = {
        answered: 0,
        unanswered: 0,
        flagged: 0,
      }
      this.questions.forEach(question => {
        if (question.answered) qStatus.answered++
        if (question.is_flagged) qStatus.flagged++
      })
      qStatus.unanswered = this.questions.length - qStatus.answered
      return qStatus
    },
    unansweredQuestions() {
      return this.questionsStatus.unanswered
    },
    answeredQuestions() {
      return this.questionsStatus.answered
    },
    flaggedQuestions() {
      return this.questionsStatus.flagged
    },
    isBeta() {
      return this.attempt?.certification?.is_beta ?? true
    },
    eventContextData() {
      return {
        certification_id: this.certification?.id,
        certification_name: this.certification?.name,
        attempt_id: this.attempt?.id,
        created: this.attempt?.created,
        expiration_date: this.attempt?.expiration_date,
        learning_area_id: this.certification?.learning_area?.id,
        learning_area_name: this.certification?.learning_area?.name,
        latest_version: this.certification?.latest_version,
        is_beta: this.certification?.is_beta,
        certification_status: this.certification?.status,
        attempt_status: this.attempt?.status,
        finished_at: this.attempt?.finished_at,
        has_passed: this.attempt?.has_passed,
        score: this.attempt?.score,
      }
    },
    thereIsPracticalPart() {
      return !!this.attempt?.practice_lab
    },
    examSteps() {
      return this.thereIsPracticalPart
        ? {
            QUESTIONS: 'Questions',
            PRACTICE: 'Hands-on Lab',
            REVIEW: 'Review',
          }
        : {
            QUESTIONS: 'Questions',
            REVIEW: 'Review',
          }
    },
    canGoToReview() {
      if (this.thereIsPracticalPart) {
        return !!this.attempt.user_lab_attempt && !this.isLoading
      }
      return !this.isLoading
    },
    mixpanelEventPayload() {
      const {
        id: certification_id,
        name: certification_name,
      } = this.attempt?.certification
      const { id: lab_id, name: lab_name, lab_type } = this.examLab

      return {
        certification_id,
        certification_name,
        lab_id,
        lab_name,
        lab_type,
        lab_context: this.context,
        ...this.examLab,
      }
    },
    isLabRunning() {
      return this.currentSession?.status === 'started'
    },
    labsRequestPayload() {
      return {
        id: this.examLab.id,
        parentId: this.attempt?.id,
        parentType: CONSTANTS.PARENT_CONTENT_TYPES_KEYS.EXAM_ATTEMPT,
      }
    },
  },
  watch: {
    currentQuestion: {
      immediate: true,
      deep: true,
      handler: 'updatePrism',
    },
    isLabRunning(value) {
      if (!value) {
        return this.currentSession?.status === 'started'
      }
    },
  },
  created() {
    this.questionsWithAnswers = this.questions
    const body = document.querySelector('body')
    body.classList.add('modal-open')
    this.runningTimeIntervalId = setInterval(this.updateCurrentTime, 1000)
    this.runningOfOutTimeIntervalId = setInterval(this.runningOutOfTime, 1000)
  },
  async mounted() {
    if (this.attempt.questions_lab || this.attempt.practice_lab) {
      await this.getRunningLab()
    }
  },
  beforeDestroy() {
    this.runningTimeIntervalId = clearInterval(this.runningTimeIntervalId)
    this.runningOfOutTimeIntervalId
      ? (this.runningOfOutTimeIntervalId = clearInterval(
          this.runningOfOutTimeIntervalId
        ))
      : null
  },
  destroyed() {
    this.$store.dispatch('certifications/clearExamStore', '')
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
  },
  methods: {
    ...mapActions('labs', ['getRunningLab']),
    setSelectedAnswers(answer_ids) {
      const { uuid: question_id } = this.currentQuestion
      this.selectedAnswers = [
        ...this.selectedAnswers,
        { question_id, answers: answer_ids },
      ]
    },
    commitAnswer(answer_ids) {
      this.setSelectedAnswers(answer_ids)
      this.setNextQuestion()
    },
    commitAnswerAndSubmit() {
      this.openAlertModal = true
    },
    async commitAnswerAndPractice() {
      this.setSelectedAnswers(this.$refs.answers.currentSelection)
      await this.submitPartialAttempt()
      this.goToStep(this.examSteps.PRACTICE)
    },
    async commitAnswerAndReview() {
      this.setSelectedAnswers(this.$refs.answers.currentSelection)
      await this.submitPartialAttempt()
      this.goToStep(this.examSteps.REVIEW)
    },
    movePreviousQuestion(answer_ids) {
      this.setSelectedAnswers(answer_ids)
      this.setPreviousQuestion()
    },
    moveToQuestion(question) {
      const index = this.questionsSorted.findIndex(
        q => q.uuid === question.uuid
      )
      this.currentPosition = index
      this.submitPartialAttempt()
    },
    async setNextQuestion() {
      if (this.currentPosition === this.totalQuestions - 1) {
        await this.submitPartialAttempt()
        this.openAlertModal = true
      } else {
        this.currentPosition = this.currentPosition + 1
        await this.submitPartialAttempt()
      }
      this.updatePrism()
    },
    async setPreviousQuestion() {
      if (this.currentPosition !== 0) {
        this.currentPosition = this.currentPosition - 1
        await this.submitPartialAttempt()
      }
      this.updatePrism()
    },
    async submitPartialAttempt() {
      this.isLoading = true
      const submission = this.generateSubmission()
      const payload = {
        voucherId: this.voucherId,
        attemptId: this.attempt.id,
        submission,
        status: CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.IN_PROGRESS,
      }
      try {
        await this.$store.dispatch('certifications/updateExamAttempt', payload)
        this.lastSavedDate = new Date()
      } catch (error) {
        this.$emit('exam-error')
      }
      this.isLoading = false
    },
    async submitAttempt() {
      this.isLoading = true
      const submission = this.generateSubmission()
      const payload = {
        voucherId: this.voucherId,
        attemptId: this.attempt.id,
        submission,
      }
      if (this.isLabRunning) {
        try {
          await this.$store.dispatch('labs/partialUpdateLabSession', {
            labId: this.examLab.id,
            sessionId: this.currentSession.id,
            status: 'terminated',
            ...this.labsRequestPayload,
          })
          await this.$store.dispatch('labs/getLabById', this.labsRequestPayload)
        } catch (error) {
          return ``
        }
      }
      try {
        await this.$store.dispatch('certifications/finishExamAttempt', payload)
        this.runningOfOutTimeIntervalId = clearInterval(
          this.runningOfOutTimeIntervalId
        )
        this.sendMixpanelEvent(
          this.eventsConstants.CERTIFICATION_ATTEMPT_FINISHED,
          {
            ...this.eventContextData,
          }
        )
      } catch (error) {
        this.$emit('exam-error')
      }
      this.scoreQuiz()
      this.isLoading = false
      this.openAlertModal = false
      if (this.isBeta) {
        this.openFinishedModal = true
      } else {
        this.showScoreModal = true
        this.sendMixpanelEvent(
          this.eventsConstants.CERTIFICATION_ATTEMPT_SCORED,
          {
            ...this.eventContextData,
          }
        )
      }
    },
    generateSubmission(feedback = '', questionId = '') {
      const { questions, selectedAnswers } = this
      const submission = questions
      submission.map(question => {
        if (question.uuid === questionId) {
          question.feedback = feedback
        }
        selectedAnswers.map(userAnswer => {
          if (userAnswer.question_id === question.uuid) {
            if (question.answers) {
              question.answers.map(answer => {
                answer.is_selected = userAnswer.answers.includes(answer.uuid)
              })
            } else {
              return [question.uuid]
            }
          }
        })
      })
      return submission
    },
    scoreQuiz() {
      this.isExamComplete = true
    },
    updatePrism() {
      this.$nextTick().then(() => {
        if (this.$refs.examcontent) {
          highlightAllUnder(this.$refs.examcontent)
          if (window.MathJax) {
            window.MathJax.Hub.Config({
              tex2jax: {
                inlineMath: [['$', '$']],
                displayMath: [['$$', '$$']],
                processEscapes: true,
                processEnvironments: true,
              },
              // Center justify equations in code and markdown cells. Elsewhere
              // we use CSS to left justify single line equations in code cells.
              displayAlign: 'center',
              'HTML-CSS': {
                styles: { '.MathJax_Display': { margin: 0 } },
                linebreaks: { automatic: true },
              },
            })
            window.MathJax.Hub.Queue([
              'Typeset',
              window.MathJax.Hub,
              this.$refs.examcontent,
            ])
          }
        }
      })
    },
    openQuestionFeedback() {
      this.feedbackTitle = `Feedback on Question ${this.currentPosition + 1}`
      this.questionFeedback = true
      this.openFeedbackModal = true
      this.feedbackHolder =
        'Did you understand the question?\nDid the question align with the course objectives?\nDid the question align with the course content?\nWas the question fair?\nDid the question stump you?'
    },
    generalFeedback() {
      this.feedbackTitle = `Do you have feedback about this exam?`
      this.questionFeedback = false
      this.openFinishedModal = false
      this.openFeedbackModal = true
      this.feedbackHolder =
        'Did you experience any issues navigating the exam platform?\nWere the instructions clearly outlined and easy to follow?\nWere the platform and features organized in a way that was easy to understand and use?\nHow sufficient were the features and tools at meeting your learning needs?\nWhat could have been better?'
    },
    flagQuestion() {
      this.currentQuestion.is_flagged = !this.currentQuestion.is_flagged
      this.submitPartialAttempt()
    },
    async saveFeedback(feedback) {
      let payload = {}
      if (this.questionFeedback) {
        const submission = this.generateSubmission(
          feedback,
          this.currentQuestion.uuid
        )
        payload = {
          voucherId: this.voucherId,
          attemptId: this.attempt.id,
          submission,
        }
      } else {
        payload = {
          voucherId: this.voucherId,
          attemptId: this.attempt.id,
          feedback,
        }
      }
      await this.$store.dispatch('certifications/updateExamAttempt', payload)
      this.lastSavedDate = new Date()
    },
    closeFeedback() {
      if (this.questionFeedback) {
        this.openFeedbackModal = false
      } else {
        this.openFeedbackModal = false
        this.openThankYouModal = true
      }
    },
    updateCurrentTime() {
      const duration = intervalToDuration({
        start: new Date(),
        end: new Date(this.attempt.expiration_date),
      })
      if (duration.days) {
        duration.hours += duration.days * 24
      }
      if (duration.hours) {
        this.currentTime = `${duration.hours}h${duration.minutes}m${duration.seconds}s`
      } else {
        this.currentTime = `${duration.minutes}m${duration.seconds}s`
      }
      this.currentDuration = duration
    },
    runningOutOfTime() {
      const result = differenceInMilliseconds(
        new Date(this.attempt.expiration_date),
        new Date()
      )
      this.runOutOfTime = Boolean(result <= 0)
    },
    updatePracticeState(state) {
      this.practiceStatus = state
    },
    goToStep(step) {
      this.currentExamStep = step
    },
    changeCurrentLabStatus(status) {
      this.currentLabStatus = status
    },
    setLabStatus() {},
    setCTADisabled(value) {
      this.canSubmit = value
    },
  },
}
</script>

<style lang="scss">
.exam-content {
  color: var(--white);
  &__modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('../../assets/images/backgrounds/dashboard_bg.jpg');
    background-size: cover;
    background-repeat: no-repeat;
    background-color: var(--dark-gray);
    z-index: 205;
  }
  &__ilu-wrapper {
    position: relative;
    overflow: hidden;
    height: 100%;
  }
  &__time-wrapper {
    max-width: 1240px;
    margin: 35px 30px;
    display: flex;
    @include tablet {
      margin: 35px auto;
      padding: 0 30px;
    }
    @include desktop {
      padding: 0;
    }
  }
  &__lab-block {
    max-width: 1240px;
    display: flex;
    flex-flow: row wrap;
    align-content: flex-start;
    align-items: flex-start;
    margin: 0 auto 20px;
    &-instructions {
      font-size: 16px;
      line-height: 24px;
      margin: 0 30px 20px;

      a {
        color: var(--white);
        text-decoration: underline;
        &:visited {
          color: var(--white);
        }
      }
      @include tablet {
        margin: 0 0 20px;
        padding: 0 30px;
      }
      @include desktop {
        padding: 0;
      }
    }
    &-lab-container {
      background: linear-gradient(
          104.99deg,
          rgba(62, 63, 73, 0.7) -13.91%,
          rgba(62, 63, 73, 0) 100.48%
        )
        var(--dark-brighter);
      border-radius: 10px;
      padding: 30px;
      padding-bottom: 0;
      &--beta {
        border-radius: 10px 10px 0 0;
      }
    }
    &-beta {
      width: 100%;
      height: 40px;
      display: flex;
      align-items: center;
      padding: 5px;
      border-radius: 0 0 10px 10px;
      background: linear-gradient(
          112.2deg,
          rgba(187, 188, 199, 0.4) -46.39%,
          rgba(187, 188, 199, 0) 112.38%
        )
        var(--dark-brighter);
      font-weight: 450;
      font-size: 12px;
      line-height: 14px;
      color: var(--light-blue-gray);
      &-pill {
        background: linear-gradient(
          111.02deg,
          rgba(18, 18, 18, 0.7) 3.86%,
          rgba(18, 18, 18, 0) 107.96%
        );
        border-radius: 20px;
        padding: 9px 14px;
        margin: 10px 15px;
      }
    }
  }
  &__wrapper {
    width: 100%;
    height: 100%;
    z-index: 206;
    overflow: auto;
    position: relative;
    &-limited {
      max-width: 1400px;
      display: flex;
      flex-flow: row wrap;
      justify-content: space-around;
      align-content: flex-start;
      align-items: flex-start;
      margin: 0 auto;
      &--layout-v2 {
        max-width: 1240px;
        background: var(--glass-mid-gray);
        border-radius: 10px;
        margin-bottom: 10%;
        .question-nav {
          background: none;
          margin: 0;
          padding: 0;
        }
      }
    }
  }
  &__content-wrapper--layout-v2 {
    padding: 30px;
    width: 100%;
    @include desktop {
      border-left: 1px solid #3e3f49;
      width: 75%;
    }
    .exam-content__content--layout-v2 {
      position: relative;
      display: flex;
      flex-flow: column;
    }
    .question__wrapper {
      padding-right: 0;
    }
  }
  &__content {
    position: relative;
    display: flex;
    margin: 0 auto;
    flex-wrap: wrap;
    padding: 40px 40px 150px 40px;
    background: white;
    background: linear-gradient(
        104.99deg,
        rgba(62, 63, 73, 0.7) -13.91%,
        rgba(62, 63, 73, 0) 100.48%
      )
      var(--dark-brighter);
    border-radius: 20px;
    position: relative;
    overflow: hidden;
    align-self: flex-start;
    @include desktop {
      flex-wrap: nowrap;
    }
    &-wrapper {
      flex: 1;
      margin: 30px;
      position: relative;
      margin-bottom: 200px;
      @include tablet {
        margin: 20px 0 200px 0;
      }
    }
  }
  &__left-column {
    width: 100%;
    padding: 30px;

    @include desktop {
      width: 310px;
    }
  }
  &__navigation {
    margin: 20px 20px;
  }
  &__alert {
    &__title {
      width: 500px;
      font-size: rem(40px);
      margin: 20px auto;
      line-height: 52px;
    }
    &__subtitle {
      text-align: center;
      font-size: rem(18px);
      line-height: 24px;
      color: var(--light-blue-gray);
      max-width: 800px;
      margin-bottom: 20px;
    }
    &--close {
      width: fit-content;
      margin: 20px auto;
    }
    &__cta {
      margin: 0 auto;
      display: block;
    }
    &__success {
      fill: #02a378;
    }
    &__warning {
      color: var(--white);
      background: linear-gradient(270deg, #ca6c06 0%, #804505 100%);
      border-radius: 10px;
      width: 91%;
      margin: 10px auto;
      padding: 10px 20px;
      display: flex;
      align-items: center;
      text-align: center;
      &-icon {
        stroke: #f8b369;
        fill: none;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 2px;
        overflow: visible;
        margin-right: 10px;
      }
    }
    &__error {
      color: var(--white);
      background: linear-gradient(270deg, #cf2121 0%, #860606 100%);
      border-radius: 10px;
      width: 60%;
      margin: 10px auto;
      padding: 10px 20px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      text-align: center;
      justify-content: center;
      &-icon {
        stroke: var(--red);
        fill: none;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 2px;
        overflow: visible;
        margin-right: 10px;
      }
    }
  }
  &__submit {
    display: flex;
    margin: 30px 0;
    justify-content: flex-end;
  }
  &__question {
    width: 100%;
    @include desktop {
      width: 40%;
    }
    &--layout-v2 {
      width: 100%;
      .question {
        &__wrapper {
          margin-bottom: 60px;
        }
        &__q {
          font-weight: 700;
          font-size: 16px;
          line-height: 24px;
          color: var(--light-blue-gray);
        }
        &__content {
          font-weight: 450;
          font-size: 20px;
          line-height: 26px;
          color: var(--white);
        }
      }
    }
  }
  &__completed {
    width: 100%;
  }
  &__loader-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
  }
  &__loader {
    width: 5rem;
  }
  &__exit__btn {
    margin-right: 20px;
  }
  &__dynamic-flag {
    float: right;
    position: relative;
    top: -30px;
    right: 25px;
  }
}
</style>
